import { Box, Button, HStack, Text } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  type CompleteExpenseBudgetItemMutation,
  useCompleteExpenseBudgetItemMutation,
} from "@src/__generated__/graphql";
import { ExpenseBudgetItemTypeEnum } from "@src/__generated__/urql-graphql";
import { ButtonLink } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import type { ExpenseItem } from "./models";

export interface IncompletedSectionProps {
  expenseItem: ExpenseItem;
  onComplete: (
    data: CompleteExpenseBudgetItemMutation["completeExpenseBudgetItem"],
  ) => void;
}

const IncompletedSection = ({
  expenseItem,
  onComplete,
}: IncompletedSectionProps) => {
  const [complete, { loading }] = useCompleteExpenseBudgetItemMutation({
    variables: { id: expenseItem.id },
    onCompleted(data) {
      if (data?.completeExpenseBudgetItem) {
        onComplete(data.completeExpenseBudgetItem);
      }
    },
    ...onError(),
  });

  const canComplete = can([
    "project_budget_completeExpenseBudgetItem_all",
    "project_budget_completeExpenseBudgetItem_team",
    "project_budget_completeExpenseBudgetItem_own",
  ]);

  return (
    <Box>
      <Text>
        <Trans>Have you received all expenses?</Trans>
      </Text>
      <HStack mt="3" spacing="3">
        <Button
          h={{
            base: "14",
            md: "9",
          }}
          colorScheme="grey"
          isDisabled={!canComplete}
          isLoading={loading}
          onClick={() => complete()}
          variant="outline"
        >
          <Text fontWeight="bold" whiteSpace="normal" wordBreak="break-all">
            {!expenseItem.project.billable ? (
              <Trans>Close this expense</Trans>
            ) : (
              <Trans>Yes and calculate final commission</Trans>
            )}
          </Text>
        </Button>

        <ButtonLink
          variant="link"
          leftIcon={<Icon name="plus" />}
          href={{
            pathname: "/expenses/create",
            query: {
              brandId: expenseItem.project.brand.id,
              projectId: expenseItem.project.id,
              budgetItemId:
                expenseItem.id +
                "-" +
                ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
            },
          }}
        >
          <Trans>Add new expense</Trans>
        </ButtonLink>
      </HStack>
    </Box>
  );
};

export default observer(IncompletedSection);
